import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Admin proposal list (T-0122 / PR #141).
 *
 *   GET /api/v1/admin/proposals?type=rubric|domain|checkpoint&status=pending
 *     → { items: [{ id, engagement_id, run_id, proposal_type, target_ref,
 *                   proposed_value, confidence, confidence_basis,
 *                   is_ai_derived_custom, status, created_at,
 *                   citations: [{ id, document_id, document_title,
 *                                  page_number, section_path, span }] }] }
 *
 * Firm-admin-scoped, cross-engagement listing of pending AI proposals
 * for the T-0114 editor Accept/Reject panels. The type alias maps to
 * the underlying `ai_proposals.proposal_type` enum:
 *
 *   rubric    → readiness_rubric
 *   domain    → health_domain_set + domain_rating
 *   checkpoint → checkpoint
 *
 * The endpoint is firm-admin only at the API layer (non-admins surface
 * as 403); engagement_lead / consultant editor modals will see an empty
 * list rather than the panel. T-0114 surfaces a tooltip noting the
 * gate.
 */

export const ADMIN_PROPOSAL_TYPES = ['rubric', 'domain', 'checkpoint'] as const;
export type AdminProposalType = (typeof ADMIN_PROPOSAL_TYPES)[number];

export const ADMIN_PROPOSAL_STATUSES = ['pending', 'accepted', 'overridden'] as const;
export type AdminProposalStatus = (typeof ADMIN_PROPOSAL_STATUSES)[number];

export const AdminProposalCitationSchema = z.object({
  id: z.string().uuid(),
  document_id: z.string().uuid(),
  document_title: z.string(),
  page_number: z.number().int().nullable(),
  section_path: z.string().nullable(),
  span: z.unknown(),
});
export type AdminProposalCitation = z.infer<typeof AdminProposalCitationSchema>;

export const AdminProposalSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  run_id: z.string().uuid().nullable(),
  proposal_type: z.string(),
  target_ref: z.unknown(),
  proposed_value: z.unknown(),
  confidence: z.number(),
  confidence_basis: z.string(),
  is_ai_derived_custom: z.boolean(),
  status: z.enum(ADMIN_PROPOSAL_STATUSES),
  created_at: z.string(),
  citations: z.array(AdminProposalCitationSchema),
});
export type AdminProposal = z.infer<typeof AdminProposalSchema>;

export const AdminProposalListResponseSchema = z.object({
  items: z.array(AdminProposalSchema),
});
export type AdminProposalListResponse = z.infer<typeof AdminProposalListResponseSchema>;

export interface ListAdminProposalsOpts {
  type: AdminProposalType;
  status?: AdminProposalStatus;
  limit?: number;
}

export function listAdminProposals(
  opts: ListAdminProposalsOpts,
): Promise<AdminProposalListResponse> {
  const query: Record<string, string | number | undefined> = { type: opts.type };
  if (opts.status) query.status = opts.status;
  if (opts.limit) query.limit = opts.limit;
  return apiClient('/api/v1/admin/proposals', AdminProposalListResponseSchema, { query });
}

export function useAdminProposalsQuery(opts: ListAdminProposalsOpts, enabled = true) {
  return useQuery({
    queryKey: [
      'admin',
      'proposals',
      opts.type,
      opts.status ?? 'pending',
      opts.limit ?? null,
    ] as const,
    queryFn: () => listAdminProposals(opts),
    enabled,
    staleTime: 15_000,
  });
}
